import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS, SPONSORS } from "@/lib/data";
import { Reveal } from "./Reveal";
import { SmokeReveal } from "./SmokeReveal";

const IMPACT_IMAGES = [
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_pahlawan-tickets/artifacts/7ww88xae_43.webp",
    alt: "Pahlawan Championship — 6 May 2023, KHTP Kulim Hi-Tech, Kedah — 43M views, 12.9M likes",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_pahlawan-tickets/artifacts/co9pa03m_53M.webp",
    alt: "Wilayah Pahlawan — 22 February 2025, Stadium Titiwangsa, KL — 53M views, 19M likes",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_pahlawan-tickets/artifacts/4qph0s7r_89m.webp",
    alt: "Wilayah Pahlawan 2.0 — 7 December 2025, Stadium Titiwangsa, KL — 89M views, 35.5M likes",
  },
];

export const Impact = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="impact" ref={ref} className="relative overflow-hidden border-y border-white/10">
      {/* parallax bg */}
      <motion.div style={{ y }} className="absolute inset-0 scale-125">
        <img src={ASSETS.arena} alt="Arena" className="h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-void via-void/85 to-void" />
      </motion.div>

      <div className="relative mx-auto max-w-6xl px-4 py-28 sm:px-8 lg:py-40">
        <SmokeReveal tint="pink" className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-4xl leading-[0.95] sm:text-6xl lg:text-7xl">
            A MOMENT OF <br />
            <span className="gradient-text text-glow-pink">COLLECTIVE IMPACT</span>
          </h2>
        </SmokeReveal>

        <Reveal delay={0.1} className="mx-auto mt-12 max-w-5xl">
          <div className="grid gap-4 sm:grid-cols-3" data-testid="impact-gallery">
            {IMPACT_IMAGES.map((img) => (
              <div key={img.src} className="overflow-hidden rounded-2xl border border-white/10 glow-ring">
                <img src={img.src} alt={img.alt} className="w-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        </Reveal>

        <div className="mx-auto mt-16 grid max-w-3xl grid-cols-2 gap-6">
          <Reveal className="rounded-2xl border border-white/10 bg-void/50 p-8 text-center backdrop-blur-sm" data-testid="impact-views">
            <div className="font-display text-5xl gradient-text sm:text-7xl">185M</div>
            <p className="mt-3 font-mono-cap text-[10px] text-slate-300">Total Views</p>
          </Reveal>
          <Reveal delay={0.1} className="rounded-2xl border border-white/10 bg-void/50 p-8 text-center backdrop-blur-sm" data-testid="impact-engagements">
            <div className="font-display text-5xl gradient-text sm:text-7xl">67.4M</div>
            <p className="mt-3 font-mono-cap text-[10px] text-slate-300">Engagements</p>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="mx-auto mt-12 max-w-2xl text-center">
          <p className="font-body text-lg text-slate-300">
            With 185 million views and 67.4 million engagements across three championship events, Pahlawan reached far beyond the arena. Our sincere
            appreciation to all partners, sponsors, media, athletes, and supporters who made this possible.
          </p>
          <p className="mt-5 font-sub text-lg font-semibold text-white">
            Thank you for being part of the journey.
          </p>
        </Reveal>

        {/* Sponsor logo placeholders */}
        <Reveal delay={0.2} className="mt-20">
          <p className="mb-6 text-center font-mono-cap text-[10px] text-neon-cyan">Media Partner</p>
          <div className="mx-auto mb-6 flex h-32 max-w-xl items-center justify-center rounded-2xl border border-neon-cyan/30 bg-white/5 p-6 glow-ring" data-testid="sponsor-main">
            <img
              src={SPONSORS.find((s) => s.main).logo}
              alt="Siakap Keli"
              className="max-h-full max-w-full object-contain"
            />
          </div>
          <p className="mb-6 text-center font-mono-cap text-[10px] text-slate-500">Our Previous Partner</p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {SPONSORS.filter((s) => !s.main).map((s) => (
              <div
                key={s.name}
                className={`flex h-24 items-center justify-center rounded-2xl border transition-all duration-300 hover:-translate-y-1 ${
                  s.big ? "p-1.5" : "p-5"
                } ${
                  s.light
                    ? "border-white/10 bg-white hover:shadow-[0_0_25px_-6px_rgba(0,247,255,0.6)]"
                    : s.whiteOutline
                      ? "border-white/60 bg-white/5 shadow-[0_0_18px_-6px_rgba(255,255,255,0.5)] hover:border-white"
                      : "border-white/10 bg-white/5 hover:border-neon-cyan/40"
                }`}
                data-testid={`sponsor-${s.name}`}
              >
                <img
                  src={s.logo}
                  alt={s.name}
                  className={`max-w-full object-contain ${s.big ? "max-h-full scale-110" : "max-h-full"}`}
                />
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
